import type { UserProfile } from "../types";
import { calcDailyTargets, calcBMR, ACTIVITY_FACTORS } from "./nutrition";

export interface ResolvedCalorieTarget {
  calories: number;
  source: "weightGoal" | "profile" | "fallback";
}

export interface ResolvedProteinTarget {
  protein_g: number;
  source: "profile" | "fallback";
}

export interface ResolvedWaterTarget {
  glasses: number;
  source: "configured" | "fallback";
}

export interface ResolvedStepsTarget {
  steps: number;
  source: "configured" | "fallback";
}

export function calcTDEE(profile: UserProfile): number {
  const bmr = calcBMR(profile.gender, profile.weight_kg, profile.height_cm, profile.age);
  return Math.round(bmr * (ACTIVITY_FACTORS[profile.activity] ?? 1.375));
}

export function calcCalorieDeficit(weeklyKg: number): number {
  return Math.min(1000, Math.round((weeklyKg * 7700) / 7));
}

export function resolveCalorieTarget(
  profile: UserProfile | null,
  weightLossGoal: { weekly_target_kg: number } | null,
): ResolvedCalorieTarget {
  if (!profile) {
    return { calories: 2000, source: "fallback" };
  }
  const tdee = calcTDEE(profile);
  if (weightLossGoal) {
    const deficit = calcCalorieDeficit(Number(weightLossGoal.weekly_target_kg));
    return { calories: Math.max(1200, tdee - deficit), source: "weightGoal" };
  }
  const targets = calcDailyTargets(profile);
  return { calories: targets.calories, source: "profile" };
}

export function resolveProteinTarget(
  profile: UserProfile | null,
): ResolvedProteinTarget {
  if (!profile) {
    return { protein_g: 50, source: "fallback" };
  }
  const targets = calcDailyTargets(profile);
  return { protein_g: targets.protein_g, source: "profile" };
}

export function resolveSugarLimit(
  profile: UserProfile | null,
): number {
  if (!profile) return 50;
  const targets = calcDailyTargets(profile);
  return Math.round(targets.calories * 0.1 / 4);
}

export const DEFAULT_WATER_TARGET = 8;
export const DEFAULT_STEPS_TARGET = 10000;

export function resolveWaterTarget(configured: number | null | undefined): ResolvedWaterTarget {
  if (configured && configured > 0) {
    return { glasses: configured, source: "configured" };
  }
  return { glasses: DEFAULT_WATER_TARGET, source: "fallback" };
}

export function resolveStepsTarget(configured: number | null | undefined): ResolvedStepsTarget {
  if (configured && configured > 0) {
    return { steps: configured, source: "configured" };
  }
  return { steps: DEFAULT_STEPS_TARGET, source: "fallback" };
}

export function targetSourceLabel(
  source: "weightGoal" | "profile" | "fallback" | "configured",
  labels: { weightGoal: string; profile: string; fallback: string; configured: string },
): string {
  switch (source) {
    case "weightGoal": return labels.weightGoal;
    case "profile": return labels.profile;
    case "configured": return labels.configured;
    default: return labels.fallback;
  }
}
